import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Star, Upload, Save, Pencil, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import api, { imgSrc } from "@/lib/api";
import { Panel, Empty, AdminField, AdminSelect } from "@/pages/admin/adminui";
import { LuxButton } from "@/components/lux";

// ---------------- PACKAGES ----------------
export function PackagesTab() {
  const [pkgs, setPkgs] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/packages").then((r) => setPkgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = { name: "", short_name: "", slug: "", price: 0, deposit: 0, currency: "B$", tagline: "",
    duration: "", locations: 1, outfits: 1, edited_images: 1, turnaround: "", description: "",
    features: [], fine_print: "", additional_image_price: 0, featured: false, active: true, starting_price: false, order: 99 };

  const save = async () => {
    const body = { ...edit, features: typeof edit.features === "string" ? edit.features.split("\n").filter(Boolean) : edit.features,
      price: Number(edit.price), deposit: Number(edit.deposit), order: Number(edit.order) };
    if (edit.id) await api.patch(`/admin/packages/${edit.id}`, body);
    else await api.post("/admin/packages", body);
    toast.success("Package saved"); setEdit(null); load();
  };
  const del = async (id) => { if (!window.confirm("Delete package?")) return; await api.delete(`/admin/packages/${id}`); load(); };

  return (
    <Panel testid="packages-tab" title="Packages" action={
      <LuxButton size="sm" onClick={() => setEdit({ ...blank })} data-testid="add-package"><Plus size={14} /> New</LuxButton>
    }>
      {pkgs.length === 0 ? <Empty>No packages.</Empty> : (
        <div className="space-y-3">
          {pkgs.map((p) => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-[#0b0d12] p-4 flex items-center justify-between gap-3" data-testid={`pkg-${p.slug}`}>
              <div className="flex items-center gap-3 min-w-0">
                {p.cover_image && <img src={p.cover_image} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{p.name} {p.featured && <Star size={12} className="inline text-[#02AACF]" fill="#02AACF" />}</p>
                  <p className="text-[#64748B] text-xs">{p.currency}{p.price} · {p.active ? "Active" : "Hidden"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setEdit({ ...p, features: (p.features || []).join("\n") })} className="text-[#94A3B8] hover:text-white" data-testid={`edit-pkg-${p.slug}`}><Pencil size={16} /></button>
                <button onClick={() => del(p.id)} className="text-[#64748B] hover:text-[#f87171]"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="package-editor">
          <DialogTitle className="sr-only">Package editor</DialogTitle>
          {edit && (<>
            <h3 className="font-display font-bold text-xl">{edit.id ? "Edit" : "New"} Package</h3>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <AdminField label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} testid="pkg-name" />
              <AdminField label="Short Name" value={edit.short_name} onChange={(e) => setEdit({ ...edit, short_name: e.target.value })} testid="pkg-short" />
              <AdminField label="Slug" value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} testid="pkg-slug" />
              <AdminField label="Tagline" value={edit.tagline} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} />
              <AdminField label="Price" type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} testid="pkg-price" />
              <AdminField label="Deposit" type="number" value={edit.deposit} onChange={(e) => setEdit({ ...edit, deposit: e.target.value })} />
              <AdminField label="Duration" value={edit.duration} onChange={(e) => setEdit({ ...edit, duration: e.target.value })} />
              <AdminField label="Turnaround" value={edit.turnaround} onChange={(e) => setEdit({ ...edit, turnaround: e.target.value })} />
              <AdminField label="Locations" value={edit.locations} onChange={(e) => setEdit({ ...edit, locations: e.target.value })} />
              <AdminField label="Outfits" value={edit.outfits} onChange={(e) => setEdit({ ...edit, outfits: e.target.value })} />
              <AdminField label="Edited Images" value={edit.edited_images} onChange={(e) => setEdit({ ...edit, edited_images: e.target.value })} />
              <AdminField label="Add'l Image Price" value={edit.additional_image_price} onChange={(e) => setEdit({ ...edit, additional_image_price: e.target.value })} />
              <AdminField label="Cover Image URL" value={edit.cover_image} onChange={(e) => setEdit({ ...edit, cover_image: e.target.value })} />
              <AdminField label="Order" type="number" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} />
            </div>
            <div className="mt-3"><label className="text-[#94A3B8] text-xs font-mono2">Description</label><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={2} className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
            <div className="mt-3"><label className="text-[#94A3B8] text-xs font-mono2">Features (one per line)</label><Textarea value={edit.features} onChange={(e) => setEdit({ ...edit, features: e.target.value })} rows={5} data-testid="pkg-features" className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
            <div className="mt-3"><label className="text-[#94A3B8] text-xs font-mono2">Fine Print</label><Textarea value={edit.fine_print} onChange={(e) => setEdit({ ...edit, fine_print: e.target.value })} rows={2} className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
            <div className="flex gap-4 mt-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-[#cbd5e1]"><input type="checkbox" checked={edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} className="accent-[#02AACF]" data-testid="pkg-featured" /> Featured</label>
              <label className="flex items-center gap-2 text-sm text-[#cbd5e1]"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="accent-[#02AACF]" data-testid="pkg-active" /> Active</label>
              <label className="flex items-center gap-2 text-sm text-[#cbd5e1]"><input type="checkbox" checked={edit.starting_price} onChange={(e) => setEdit({ ...edit, starting_price: e.target.checked })} className="accent-[#02AACF]" data-testid="pkg-starting" /> "Starting price" (shows "From")</label>
            </div>
            <LuxButton className="mt-5 w-full" onClick={save} data-testid="save-package"><Save size={14} /> Save Package</LuxButton>
          </>)}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

// ---------------- MEDIA ----------------
export function MediaTab() {
  const [images, setImages] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [uploadCat, setUploadCat] = useState("Lifestyle");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    const params = cat === "All" ? {} : { category: cat };
    api.get("/admin/media", { params }).then((r) => setImages(r.data)).catch(() => {});
  };
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data.map((c) => c.name))).catch(() => {}); }, []);
  useEffect(() => { load(); }, [cat]); // eslint-disable-line

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    fd.append("category", uploadCat);
    try {
      await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} image(s) uploaded`); load();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const del = async (id) => { await api.delete(`/admin/media/${id}`); load(); };
  const toggleFeatured = async (img) => { await api.patch(`/admin/media/${img.id}`, { featured: !img.featured }); load(); };
  const changeCat = async (img, c) => { await api.patch(`/admin/media/${img.id}`, { category: c }); load(); };

  return (
    <Panel testid="media-tab" title="Portfolio Media">
      <div className="rounded-xl border-2 border-dashed border-white/15 p-6 text-center mb-5"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} data-testid="upload-dropzone">
        <Upload className="mx-auto text-[#02AACF]" size={26} />
        <p className="text-white mt-2 text-sm">Drag & drop images here, or</p>
        <div className="flex items-center justify-center gap-3 mt-3 flex-wrap">
          <div className="w-40"><AdminSelect value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} options={cats.length ? cats : ["Lifestyle"]} testid="upload-category" /></div>
          <LuxButton size="sm" onClick={() => fileRef.current.click()} disabled={uploading} data-testid="upload-button">{uploading ? "Uploading…" : "Choose Files"}</LuxButton>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="file-input" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {["All", ...cats].map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`whitespace-nowrap text-xs font-mono2 px-3 py-1.5 rounded-full border ${cat === c ? "bg-[#02AACF] border-[#02AACF] text-white" : "border-white/15 text-white/60"}`}>{c}</button>
        ))}
      </div>

      {images.length === 0 ? <Empty>No images.</Empty> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((img) => (
            <div key={img.id} className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#0b0d12]" data-testid={`media-${img.id}`}>
              <img src={imgSrc(img)} alt={img.alt_text} className="w-full aspect-square object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                <div className="flex justify-end gap-1">
                  <button onClick={() => toggleFeatured(img)} className={`p-1.5 rounded-lg ${img.featured ? "bg-[#02AACF] text-white" : "bg-black/50 text-white/70"}`} data-testid={`feature-${img.id}`}><Star size={13} /></button>
                  <button onClick={() => del(img.id)} className="p-1.5 rounded-lg bg-black/50 text-[#f87171]" data-testid={`delete-media-${img.id}`}><Trash2 size={13} /></button>
                </div>
                <select value={img.category} onChange={(e) => changeCat(img, e.target.value)} className="text-[10px] bg-black/70 text-white rounded px-1 py-1 border border-white/20">
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// ---------------- TESTIMONIALS ----------------
export function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const blank = { name: "", company: "", project: "", quote: "", image: "", featured: true, order: 99 };
  const save = async () => {
    if (edit.id) await api.patch(`/admin/testimonials/${edit.id}`, edit);
    else await api.post("/admin/testimonials", edit);
    toast.success("Saved"); setEdit(null); load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };
  return (
    <Panel testid="testimonials-tab" title="Testimonials" action={<LuxButton size="sm" onClick={() => setEdit({ ...blank })} data-testid="add-testimonial"><Plus size={14} /> New</LuxButton>}>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} className="rounded-xl border border-white/10 bg-[#0b0d12] p-4 flex justify-between gap-3">
            <div><p className="text-white font-semibold">{t.name}</p><p className="text-[#64748B] text-xs line-clamp-1">{t.quote}</p></div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEdit(t)} className="text-[#94A3B8] hover:text-white"><Pencil size={15} /></button>
              <button onClick={() => del(t.id)} className="text-[#64748B] hover:text-[#f87171]"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <Empty>No testimonials.</Empty>}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-lg" data-testid="testimonial-editor">
          <DialogTitle className="sr-only">Testimonial editor</DialogTitle>
          {edit && (<>
            <h3 className="font-display font-bold text-xl">Testimonial</h3>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <AdminField label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} testid="t-name" />
              <AdminField label="Company" value={edit.company} onChange={(e) => setEdit({ ...edit, company: e.target.value })} />
              <AdminField label="Project" value={edit.project} onChange={(e) => setEdit({ ...edit, project: e.target.value })} />
              <AdminField label="Image URL" value={edit.image} onChange={(e) => setEdit({ ...edit, image: e.target.value })} />
            </div>
            <div className="mt-3"><label className="text-[#94A3B8] text-xs font-mono2">Quote</label><Textarea value={edit.quote} onChange={(e) => setEdit({ ...edit, quote: e.target.value })} rows={3} data-testid="t-quote" className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
            <LuxButton className="mt-4 w-full" onClick={save} data-testid="save-testimonial">Save</LuxButton>
          </>)}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

// ---------------- FAQ ----------------
export function FaqTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/faqs").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const blank = { question: "", answer: "", order: 99, active: true };
  const save = async () => {
    if (edit.id) await api.patch(`/admin/faqs/${edit.id}`, edit);
    else await api.post("/admin/faqs", edit);
    toast.success("Saved"); setEdit(null); load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/faqs/${id}`); load(); };
  return (
    <Panel testid="faq-tab" title="FAQ" action={<LuxButton size="sm" onClick={() => setEdit({ ...blank })} data-testid="add-faq"><Plus size={14} /> New</LuxButton>}>
      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} className="rounded-xl border border-white/10 bg-[#0b0d12] p-4 flex justify-between gap-3">
            <p className="text-white text-sm">{f.question}</p>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEdit(f)} className="text-[#94A3B8] hover:text-white"><Pencil size={15} /></button>
              <button onClick={() => del(f.id)} className="text-[#64748B] hover:text-[#f87171]"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <Empty>No FAQs.</Empty>}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-lg" data-testid="faq-editor">
          <DialogTitle className="sr-only">FAQ editor</DialogTitle>
          {edit && (<>
            <h3 className="font-display font-bold text-xl">FAQ</h3>
            <AdminField label="Question" value={edit.question} onChange={(e) => setEdit({ ...edit, question: e.target.value })} testid="faq-q" />
            <div className="mt-3"><label className="text-[#94A3B8] text-xs font-mono2">Answer</label><Textarea value={edit.answer} onChange={(e) => setEdit({ ...edit, answer: e.target.value })} rows={4} data-testid="faq-a" className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
            <LuxButton className="mt-4 w-full" onClick={save} data-testid="save-faq">Save</LuxButton>
          </>)}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

// ---------------- PROJECTS ----------------
export function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [media, setMedia] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => {
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/admin/media").then((r) => setMedia(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data.map((c) => c.name))).catch(() => {}); }, []);
  const blank = { name: "", category: "Junkanoo", client: "", location: "", date: "", description: "", cover_image: "", featured: true, published: true, order: 99 };
  const save = async () => {
    if (edit.id) { await api.patch(`/admin/projects/${edit.id}`, edit); toast.success("Project saved"); load(); }
    else { const r = await api.post("/admin/projects", edit); toast.success("Project created — now assign photos"); setEdit({ ...edit, id: r.data.id, slug: r.data.slug }); load(); }
  };
  const del = async (id) => { if (!window.confirm("Delete project?")) return; await api.delete(`/admin/projects/${id}`); setEdit(null); load(); };
  const toggleImage = async (img) => {
    const assigned = img.project_id === edit.id;
    await api.patch(`/admin/media/${img.id}`, { project_id: assigned ? null : edit.id });
    setMedia((m) => m.map((x) => x.id === img.id ? { ...x, project_id: assigned ? null : edit.id } : x));
  };
  return (
    <Panel testid="projects-tab" title="Featured Projects" action={<LuxButton size="sm" onClick={() => setEdit({ ...blank })} data-testid="add-project"><Plus size={14} /> New</LuxButton>}>
      {projects.length === 0 ? <Empty>No projects yet. Create one and assign photos to build its gallery.</Empty> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {projects.map((p) => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-[#0b0d12] overflow-hidden" data-testid={`project-${p.slug}`}>
              {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-32 object-cover" />}
              <div className="p-4 flex justify-between items-start gap-2">
                <div><p className="text-white font-semibold">{p.name}</p><p className="text-[#64748B] text-xs">{p.category} · {p.published ? "Published" : "Hidden"}{p.featured ? " · Featured" : ""}</p></div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => window.open(`/project/${p.slug}`, "_blank")} className="text-[#94A3B8] hover:text-white"><ExternalLink size={15} /></button>
                  <button onClick={() => setEdit(p)} className="text-[#94A3B8] hover:text-white"><Pencil size={15} /></button>
                  <button onClick={() => del(p.id)} className="text-[#64748B] hover:text-[#f87171]"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-editor">
          <DialogTitle className="sr-only">Project editor</DialogTitle>
          {edit && (<>
            <h3 className="font-display font-bold text-xl">{edit.id ? "Edit" : "New"} Project</h3>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <AdminField label="Project Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} testid="proj-name" />
              <AdminSelect label="Category" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} options={cats.length ? cats : ["Junkanoo"]} testid="proj-category" />
              <AdminField label="Client" value={edit.client} onChange={(e) => setEdit({ ...edit, client: e.target.value })} />
              <AdminField label="Location" value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} />
              <AdminField label="Date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} />
              <AdminField label="Cover Image URL" value={edit.cover_image} onChange={(e) => setEdit({ ...edit, cover_image: e.target.value })} />
            </div>
            <div className="mt-3"><label className="text-[#94A3B8] text-xs font-mono2">Description</label><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={3} className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" /></div>
            <div className="flex gap-4 mt-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-[#cbd5e1]"><input type="checkbox" checked={edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} className="accent-[#02AACF]" data-testid="proj-featured" /> Featured on homepage</label>
              <label className="flex items-center gap-2 text-sm text-[#cbd5e1]"><input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} className="accent-[#02AACF]" /> Published</label>
            </div>
            <LuxButton className="mt-4 w-full" onClick={save} data-testid="save-project"><Save size={14} /> {edit.id ? "Save Project" : "Create Project"}</LuxButton>
            {edit.id && (<>
              <div className="h-px bg-white/10 my-5" />
              <p className="font-mono2 text-xs text-[#64748B] uppercase mb-3">Assign photos ({media.filter((m) => m.project_id === edit.id).length} in gallery)</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                {media.map((img) => {
                  const on = img.project_id === edit.id;
                  return (
                    <button key={img.id} onClick={() => toggleImage(img)} data-testid={`assign-${img.id}`} className={`relative rounded-lg overflow-hidden border-2 aspect-square ${on ? "border-[#02AACF]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                      <img src={imgSrc(img)} alt="" className="w-full h-full object-cover" />
                      {on && <span className="absolute top-1 right-1 bg-[#02AACF] rounded-full p-0.5"><Check size={11} className="text-white" /></span>}
                    </button>
                  );
                })}
              </div>
            </>)}
          </>)}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

// ---------------- AVAILABILITY ----------------
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export function AvailabilityTab() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/availability").then((r) => setS(r.data.settings)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const save = async () => {
    await api.patch("/admin/availability", {
      ...s, interval_minutes: Number(s.interval_minutes), buffer_minutes: Number(s.buffer_minutes),
      min_notice_hours: Number(s.min_notice_hours), max_advance_days: Number(s.max_advance_days),
    });
    toast.success("Availability saved");
  };
  const toggleDay = (i) => {
    const wd = s.working_days.includes(i) ? s.working_days.filter((d) => d !== i) : [...s.working_days, i];
    setS({ ...s, working_days: wd });
  };
  return (
    <Panel testid="availability-tab" title="Booking Availability">
      <div>
        <label className="text-[#94A3B8] text-xs font-mono2">Working Days</label>
        <div className="flex gap-2 mt-2 flex-wrap">
          {DAYS.map((d, i) => (
            <button key={d} onClick={() => toggleDay(i)} data-testid={`workday-${d}`}
              className={`text-xs font-mono2 px-3 py-2 rounded-full border ${s.working_days.includes(i) ? "bg-[#02AACF] border-[#02AACF] text-white" : "border-white/15 text-white/60"}`}>{d}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <AdminField label="Start Time (HH:MM)" value={s.start_time} onChange={(e) => setS({ ...s, start_time: e.target.value })} testid="start-time" />
        <AdminField label="End Time (HH:MM)" value={s.end_time} onChange={(e) => setS({ ...s, end_time: e.target.value })} testid="end-time" />
        <AdminField label="Interval (minutes)" type="number" value={s.interval_minutes} onChange={(e) => setS({ ...s, interval_minutes: e.target.value })} />
        <AdminField label="Buffer (minutes)" type="number" value={s.buffer_minutes} onChange={(e) => setS({ ...s, buffer_minutes: e.target.value })} />
        <AdminField label="Min Notice (hours)" type="number" value={s.min_notice_hours} onChange={(e) => setS({ ...s, min_notice_hours: e.target.value })} />
        <AdminField label="Max Advance (days)" type="number" value={s.max_advance_days} onChange={(e) => setS({ ...s, max_advance_days: e.target.value })} />
      </div>
      <LuxButton className="mt-5" onClick={save} data-testid="save-availability"><Save size={14} /> Save Availability</LuxButton>
      <p className="text-[#64748B] text-xs mt-3">Block individual dates from the Calendar tab.</p>
    </Panel>
  );
}

// ---------------- SETTINGS ----------------
export function SettingsTab() {
  const [s, setS] = useState(null);
  const [wave, setWave] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/wave/status").then((r) => setWave(r.data)).catch(() => setWave({ connected: false, configured: false })); }, []);
  if (!s) return null;
  const save = async () => { await api.patch("/admin/settings", s); toast.success("Settings saved"); };
  return (
    <Panel testid="settings-tab" title="Site Settings">
      <div className="grid sm:grid-cols-2 gap-3">
        <AdminField label="Instagram URL" value={s.instagram} onChange={(e) => setS({ ...s, instagram: e.target.value })} testid="set-instagram" />
        <AdminField label="TikTok URL" value={s.tiktok} onChange={(e) => setS({ ...s, tiktok: e.target.value })} />
        <AdminField label="Facebook URL" value={s.facebook} onChange={(e) => setS({ ...s, facebook: e.target.value })} />
        <AdminField label="YouTube URL" value={s.youtube} onChange={(e) => setS({ ...s, youtube: e.target.value })} />
        <AdminField label="Public Email" value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} />
        <AdminField label="Phone" value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} />
        <AdminField label="About Portrait URL" value={s.about_portrait} onChange={(e) => setS({ ...s, about_portrait: e.target.value })} />
      </div>
      <div className="mt-4 space-y-3">
        {[["privacy_policy", "Privacy Policy"], ["terms", "Terms & Conditions"], ["booking_policy", "Booking Policy"]].map(([k, l]) => (
          <div key={k}>
            <label className="text-[#94A3B8] text-xs font-mono2">{l}</label>
            <Textarea value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} rows={3} data-testid={`set-${k}`} className="mt-1.5 bg-[#0b0d12] border-white/10 text-white" />
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl border border-white/10 bg-[#0b0d12] p-4" data-testid="wave-integration-status">
        <p className="font-mono2 text-xs tracking-[0.25em] text-[#02AACF] uppercase mb-2">Wave Integration</p>
        {!wave ? (
          <p className="text-[#64748B] text-sm">Checking connection…</p>
        ) : wave.connected ? (
          <p className="text-sm text-[#cbd5e1] flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400 inline-block" data-testid="wave-status-dot" /> Connected to <span className="text-white font-semibold">{wave.business_name}</span> · {wave.currency || "USD"}
          </p>
        ) : (
          <p className="text-sm text-[#f87171] flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#f87171] inline-block" /> {wave.configured ? `Not connected${wave.error ? " — " + wave.error : ""}` : "Not configured"}
          </p>
        )}
        <p className="text-[#64748B] text-xs mt-2">Your secret access token is stored securely on the server and is never displayed here.</p>
      </div>

      <LuxButton className="mt-5" onClick={save} data-testid="save-settings"><Save size={14} /> Save Settings</LuxButton>
    </Panel>
  );
}


// ---------------- INSTAGRAM ----------------
export function InstagramTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const load = () => api.get("/admin/instagram").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const blank = { image_url: "", storage_path: "", link: "", caption: "", order: 99, active: true };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/instagram/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((e) => ({ ...e, storage_path: r.data.path, image_url: "" }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!edit.storage_path && !edit.image_url) { toast.error("Add an image first"); return; }
    const body = { ...edit, order: Number(edit.order) };
    if (edit.id) await api.patch(`/admin/instagram/${edit.id}`, body);
    else await api.post("/admin/instagram", body);
    toast.success("Post saved"); setEdit(null); load();
  };
  const del = async (id) => { if (!window.confirm("Remove this post?")) return; await api.delete(`/admin/instagram/${id}`); load(); };

  return (
    <Panel testid="instagram-tab" title="Instagram Feed" action={
      <LuxButton size="sm" onClick={() => setEdit({ ...blank })} data-testid="add-instagram"><Plus size={14} /> New Post</LuxButton>
    }>
      {items.length === 0 ? <Empty>No Instagram posts yet. Add your best shots to show a fresh, live-looking feed on the homepage.</Empty> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((p) => (
            <div key={p.id} className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#0b0d12]" data-testid={`ig-${p.id}`}>
              <img src={imgSrc(p, 400)} alt={p.caption || ""} className={`w-full aspect-square object-cover ${p.active ? "" : "opacity-40"}`} loading="lazy" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-2 gap-1">
                <button onClick={() => setEdit(p)} className="p-1.5 rounded-lg bg-black/50 text-white/80" data-testid={`edit-ig-${p.id}`}><Pencil size={13} /></button>
                <button onClick={() => del(p.id)} className="p-1.5 rounded-lg bg-black/50 text-[#f87171]" data-testid={`delete-ig-${p.id}`}><Trash2 size={13} /></button>
              </div>
              {!p.active && <span className="absolute top-2 left-2 text-[10px] font-mono2 bg-black/70 text-white/70 px-2 py-0.5 rounded-full">Hidden</span>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-lg" data-testid="instagram-editor">
          <DialogTitle className="sr-only">Instagram post editor</DialogTitle>
          {edit && (<>
            <h3 className="font-display font-bold text-xl">{edit.id ? "Edit" : "New"} Instagram Post</h3>
            <div className="mt-4 flex items-center gap-4">
              <div className="h-24 w-24 rounded-lg overflow-hidden border border-white/10 bg-[#0b0d12] shrink-0">
                {(edit.storage_path || edit.image_url)
                  ? <img src={imgSrc(edit, 300)} alt="" className="h-full w-full object-cover" />
                  : <div className="h-full w-full flex items-center justify-center text-[#64748B] text-[11px]">No image</div>}
              </div>
              <div className="flex-1">
                <LuxButton size="sm" onClick={() => fileRef.current.click()} disabled={uploading} data-testid="ig-upload"><Upload size={14} /> {uploading ? "Uploading…" : "Upload Image"}</LuxButton>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} data-testid="ig-file-input" />
                <p className="text-[#64748B] text-[11px] mt-2">Square images look best.</p>
              </div>
            </div>
            <div className="mt-3"><AdminField label="Or paste image URL" value={edit.image_url} onChange={(e) => setEdit({ ...edit, image_url: e.target.value, storage_path: "" })} testid="ig-image-url" /></div>
            <div className="mt-3"><AdminField label="Instagram Post URL (link)" value={edit.link} onChange={(e) => setEdit({ ...edit, link: e.target.value })} placeholder="https://www.instagram.com/p/..." testid="ig-link" /></div>
            <div className="mt-3"><AdminField label="Caption (optional)" value={edit.caption} onChange={(e) => setEdit({ ...edit, caption: e.target.value })} /></div>
            <div className="mt-3 flex items-end gap-4 flex-wrap">
              <div className="w-28"><AdminField label="Order" type="number" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} testid="ig-order" /></div>
              <label className="flex items-center gap-2 text-sm text-[#cbd5e1] pb-2.5"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="accent-[#02AACF]" data-testid="ig-active" /> Show on homepage</label>
            </div>
            <LuxButton className="mt-5 w-full" onClick={save} data-testid="save-instagram"><Save size={14} /> Save Post</LuxButton>
          </>)}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
